import { createSignal, type JSX } from "solid-js";
import { storeNotification } from "@/client";

export type ToastType = "success" | "info" | "warning" | "error";

export type ToastPosition =
	| "top-left"
	| "top-right"
	| "top-center"
	| "bottom-left"
	| "bottom-right"
	| "bottom-center";

export type Toast = {
	id: string;
	type: ToastType;
	title: string;
	description?:
		| string
		| (({
				toast,
				handleClose,
		  }: {
				toast: Toast;
				handleClose: () => void;
		  }) => JSX.Element);
	duration?: number; // Auto-close time in ms
	position?: ToastPosition;
	// Is clip actively shown?
	active: boolean;
	offset?: [number, number]; // [x, y] offset relative to the toast's position (e.g., for bottom-left, x is from left, y is from bottom)
	store?: boolean;
	storeDescription?: string;
	storeType?: ToastType;
	revert_action?: Record<string, unknown>;
	/** Material Symbol name; overrides the default for the toast type. */
	symbol?: string;
	/** Material Symbol fill. Defaults to filled. */
	filled?: boolean;
};

const SESSION_STORAGE_KEY = "gvtlabs-toasts";

export const NOTIFICATION_ADDED_EVENT = "notificationAdded";

/**
 * Dispatch a custom event to notify components that a notification has been added.
 */
const dispatchNotificationAdded = () => {
	if (typeof window !== "undefined") {
		window.dispatchEvent(new CustomEvent(NOTIFICATION_ADDED_EVENT));
	}
};

// Load toasts from sessionStorage on initialization
const loadToastsFromStorage = (): Toast[] => {
	if (typeof window === "undefined" || !window.sessionStorage) {
		return [];
	}

	try {
		const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
		if (stored) {
			return JSON.parse(stored) as Toast[];
		}
	} catch (error) {
		console.error("Failed to load toasts from sessionStorage:", error);
	}
	return [];
};

// Save toasts to sessionStorage
const saveToastsToStorage = (toasts: Toast[]) => {
	if (typeof window === "undefined" || !window.sessionStorage) {
		return;
	}

	try {
		sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(toasts));
	} catch (error) {
		console.error("Failed to save toasts to sessionStorage:", error);
	}
};

// Initialize store with toasts from sessionStorage
const [toasts, setToasts] = createSignal<Toast[]>(loadToastsFromStorage());

export const toastStore = {
	get toasts() {
		return toasts();
	},
	getActiveToasts: () => {
		return toasts().filter((t) => t.active === true);
	},
	addToast: async (toast: Omit<Toast, "id">) => {
		const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
		const newToast: Toast = {
			...toast,
			id,
			position: toast.position ?? "bottom-left",
			duration: toast.duration ?? 5000,
		};
		const currentToasts = toasts();
		const updated = [...currentToasts, newToast];
		setToasts(updated);
		saveToastsToStorage(updated);
		if (newToast.store) {
			const storedDescription =
				newToast.storeDescription ??
				(typeof newToast.description === "string"
					? newToast.description
					: undefined);
			const response = await storeNotification({
				body: {
					title: newToast.title,
					description: storedDescription,
					revert_action: newToast.revert_action,
					status: "active",
					type: newToast.storeType ?? newToast.type,
				},
				headers: {
					"Content-Type": "application/json",
				},
			});
			if (!response.error) {
				dispatchNotificationAdded();
			}
		}
	},
	removeToast: (id: string) => {
		const currentToasts = toasts();
		const filtered = currentToasts.filter((t) => t.id !== id);
		setToasts(filtered);
		saveToastsToStorage(filtered);
	},
	hideToast: (id: string) => {
		const currentToasts = toasts();
		const updated = currentToasts.map((t) =>
			t.id === id ? { ...t, active: false } : t,
		);
		setToasts(updated);
		saveToastsToStorage(updated);
	},
	setActiveToast: (id: string, active: boolean) => {
		const currentToasts = toasts();
		const updated = currentToasts.map((t) =>
			t.id === id ? { ...t, active } : t,
		);
		setToasts(updated);
		saveToastsToStorage(updated);
	},
	clearToasts: () => {
		setToasts([]);
		if (typeof window !== "undefined" && window.sessionStorage) {
			sessionStorage.removeItem(SESSION_STORAGE_KEY);
		}
	},
};
